/* eslint-disable prefer-template */
import { Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { v4 as uuid } from 'uuid';
import { Subscription } from 'rxjs/index';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { SentryErrorHandler } from '@services/sentry-logger';
import { CurrentComponentService } from '@services/current-component';
import { WindowRefService } from '@services/window-ref-service';
import { ChatProgressService } from '@services/chat-progress-service';
import { TimeService } from '@services/time-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';

import { InputType, SingleSelectorService } from '@shared/single-selector/single-selector.service';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { FilePickerComponent } from '@components/file-picker/file-picker.component';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NotificationPermissionSheetComponent } from
  '@shared/bottom-sheet-layouts/notification-permission-sheet/notification-permission-sheet.component';
import { LocalStorageService } from '@services/local-storage-service';
import { LanguageChangeComponent } from '@shared/bottom-sheet-layouts/language-change/language-change.component';
import { GetFeedbackSheetComponent } from '@shared/bottom-sheet-layouts/get-feedback-sheet/get-feedback-sheet.component';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { ProgressConfig, ProgressConfigType } from './helper/progress-config';
import { CrossSellRegimenService } from '@services/cross-sell-regimen-service/cross-sell-regimen.service';
import { take } from 'rxjs/operators';
import { MoreInsightsBottomsheetComponent } from '@shared/bottom-sheet-layouts/more-insights-bottomsheet/more-insights-bottomsheet.component';
import { AppConfig } from '../app.config';
import { RegimenExpiryService } from '@services/regimen-expiry-service/regimen-expiry.service';
import { Experiment } from '@cureskin/api-client/src/server-api';
import { RegimenService } from '@services/regimen-service/regimen.service';

type BotReplyBackoffTimeType = {t1: number, t2: number};

@Component({
  selector: 'chatV2-view',
  templateUrl: './chatV2-view.html',
  standalone: false,
})
export class V2ChatComponent {
  isTabActive: boolean;
  @ViewChild('messageContainer', { static: false }) messageContainer: ElementRef;
  @ViewChild('unreadMessageDivider', { static: false }) unreadMessageDivider: ElementRef;
  @ViewChild(FilePickerComponent, { static: false }) filePickerComponent: FilePickerComponent;
  chats: Array<any> = [];
  startTimeOfBotReply: any;
  @Input('hideNavBar') hideNavBar: boolean;
  @Input('disableChat') disableChat: boolean;
  user: any;
  currentUser: Table.User;
  imageModal: any = {};
  isNotificationPermissionEnabled: any;
  @Input('where') where: any;
  @Input('dataFunctionName') dataFunctionName: string = 'getAssistantMessage';
  @Input('title') title: string;
  selectedChatType: any;
  @Input('chatScrollErrorY') chatScrollErrorY: number = 0;
  @Input('chatUniqueObject') set setChatUniqueObject(chatUniqueObject: any) {
    if (this.selectedChatType && chatUniqueObject && this.selectedChatType.id !== chatUniqueObject.id) {
      this.selectedChatType = chatUniqueObject;
      this.window.nativeWindow.location.reload();
    } else {
      this.selectedChatType = chatUniqueObject;
    }
  }
  @Output('chatScrollPossible') chatScrollPossible: EventEmitter<void> = new EventEmitter();
  @Output('chatLoadingStatus') chatLoadingStatus: EventEmitter<boolean> = new EventEmitter();
  @Output('botReplyInProcessChange') botReplyInProcessChange: EventEmitter<boolean> = new EventEmitter();
  isInternalUser: boolean;
  chatPayload: { limit?: number, loading: { old: boolean, new: boolean }, noMoreData?: boolean, input?: string } = {
    limit: 100,
    loading: {
      old: false,
      new: false,
    },
    noMoreData: false,
    input: undefined,
  };
  ui: {
    enableScrollToTop?: boolean;
    enableScrollToBottom: boolean;
    botReplyInProcess: boolean,
    userResponseSaveInProcess: boolean,
    botReplyTimeSeries: BotReplyBackoffTimeType,
    chatScrollTop?: number,
    chatInputEnable?: boolean;
  };
  waitForBotReplyTimer: any;
  responseModes: any[];
  subscriptions: Array<Subscription> = [];
  allocatedDoctor: any;
  interval: any;
  timer: any = {};
  experiments: Array<any> = [];
  allowAnimation: boolean = false;
  latestMessage: any;
  isHelpfull: boolean;
  @ViewChild('assistantPage', { static: false }) assistantPage: ElementRef;
  months: Array<any> = [];
  lastVisitedTime: Date;
  multiImageData: string[] = [];
  scrollThrottle: any;
  showCallPopup: boolean;
  afterSaveFeedback: boolean;
  dataLoadedTwice: boolean;
  startTimer:number;
  callBackExperimentEnable: boolean = false;
  newHelpTreeExperiment: boolean = false;
  newSupportQuestionId: string;
  disableChatInput: boolean = false;
  photoType: string;
  progressConfig: ProgressConfigType = ProgressConfig;
  progress: number = 0;
  processedQuestions: Set<any> = new Set();
  isTicketClosedBeforeTwoDays: boolean = false;
  inTransitUser: boolean = false;
  regimens: Array<any> = [];
  crossSellRegimenV2Experiment: any;
  showNewCrossSellDiscountMessage: boolean = false;
  crossSellDiscount: number;
  crossSellDeadline: Date;
  isNotIOSDevice: boolean;
  fromSelfCheckupBanner: boolean;
  followUp: any;
  followUpChatCompleted: boolean = false;
  unpaidUserDoctorCallExperiment: boolean = false;
  isRegimenExpiredExperimentEnabled: boolean = false;
  isPaidUser: boolean = false;
  acqRebranding2025: boolean = false;
  isSkinReportLoadFixesEnabled: boolean = false;
  botReplyTimeSeries: BotReplyBackoffTimeType = { t1: 1000, t2: 1000 };
  whatDoCureskinMembersSayQuestionId: string = 'whatDoCureSkinMembersSay';
  whatDoCureskinMembersSayMessageInEnglish: string = 'What do Cureskin members say?';
  chatSkipLastQuestionsExperiment: Experiment;
  userFirstName: string;

  constructor(
    private conn: ConnectionService,
    private router: Router,
    private broadcast: BroadcastService,
    public appConfig: AppConfig,
    private route: ActivatedRoute,
    private appBridge: AppWebBridgeService,
    private zone: NgZone,
    private window: WindowRefService,
    private eventLogger: EventLoggerService,
    private timeService: TimeService,
    private currentComponent: CurrentComponentService,
    private sentry: SentryErrorHandler,
    private broadcastService: BroadcastService,
    private singleSelectorService: SingleSelectorService,
    private chatProgressService: ChatProgressService,
    private commonUtilityHelper: CommonUtilityHelper,
    private bottomSheet: MatBottomSheet,
    private localStorageService: LocalStorageService,
    private readonly consultationSessionService: ConsultationSessionService,
    private readonly crossSellRegimenService: CrossSellRegimenService,
    private readonly regimenExpiryService: RegimenExpiryService,
    private readonly regimenService: RegimenService,
    // eslint-disable-next-line new-cap
    @Inject(DOCUMENT) private document: Document) {
    this.currentComponent.set(this);
  }

  async ngOnInit(): Promise<any> {
    this.chatLoadingStatus.emit(true);
    this.startTimer = new Date().getTime();
    this.currentComponent.set(this);
    this.isInternalUser = this.conn.isInternalUser();
    this.currentUser = this.conn.getCurrentUser();
    this.user = this.conn.getActingUser();
    this.userFirstName = this.user?.get('PatientName')?.split(' ')[0];
    this.isPaidUser = this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED;
    this.isNotIOSDevice = (await this.appBridge?.requestOSInformation()) !== 'iOS';
    await this.fetchActiveExperiments();
    this.regimens = (await this.conn.fetchRegimens(null, true)).filter((each: any): boolean => !each.expired);
    this.months = this.appConfig.Shared.Months.map((each: any): void => each.name);
    this.fromSelfCheckupBanner = this.route.snapshot.queryParamMap?.get('fromSelfCaptureBanner') === 'true';
    this.subscriptions.push(this.broadcast.on<string>('NEW_MESSAGE_RECEIVED')
      .subscribe((): void => this.zone.run((): void => {
        if (!this.chatPayload.loading.old) this.loadNewChat();
      })));
    await this.initialize();
    const { showBanner, discount, deadline }: {
      showBanner: boolean,
      discount: number,
      deadline: Date
    } = this.crossSellRegimenService.evaluateCrossSellProps(
      this.regimens, this.crossSellRegimenV2Experiment,
    );
    this.showNewCrossSellDiscountMessage = showBanner;
    this.crossSellDiscount = discount;
    this.crossSellDeadline = deadline;
    if (!this.user) {
      this.user = await this.conn.getActingUser();
      this.selectedChatType = await this.conn.findSupportTicketById(this.route.snapshot.params.id, { translate: true });
      this.where = { supportTicket: this.selectedChatType };
    }
    this.inTransitUser = this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.PROCESSING;
    this.lastVisitedTime = this.selectedChatType.get('lastVisited');
    await this.loadOldChat();
    this.messageContainer.nativeElement.addEventListener('scroll', (event: any): void => this.scrollListener(event));
    /**
     * we log the last visited time of ticket in both ngOnInit and ngOnDestroy.
     * 1. In some case if ngOnDestroy doesn't update it (if ppl directly close app), we can atleast use ngOnInit time.
     * 2. ngOnDestroy takes delay to log it, meanwhile if user go back to home screen of support,
     *    there we need lastVisited time to determine new messages logic. So we can atleast use ngOnInit time for that moment.
     */
    if (!this.isInternalUser && this.selectedChatType?.className === 'SupportTicket') {
      this.conn.updateUserLastActiveDetails(this.selectedChatType.id);
    }
    if (this.hideNavBar) {
      // send with a delay of 1000 milliseconds so that page end event finish its notification.
      setTimeout((): void => {
        this.appBridge.notifyAssistantLoaded('consultationSession');
      }, 1000);
    }
    const time = new Date().getTime() - this.startTimer;
    this.checkTicketClosedDays();
    if (this.selectedChatType?.className === Table.ConsultationSession.className) {
      this.consultationSessionService.updateConsultationDetailsInLocalStorage(this.selectedChatType);
    }
    if (this.selectedChatType?.className === 'FollowUp') this.followUp = await this.conn.findRecentFollowUp();
    this.eventLogger.trackEvent('chat_screen_time', { timeInMillSec: time });
  }

  checkTicketClosedDays(): any {
    const completedAt = this.selectedChatType?.get('completedAt') as Date;
    const diffInCompletedAt = this.timeService.differenceInDays(new Date(), completedAt);
    this.eventLogger.trackEvent('difference_in_completed_at_date', { timeInDays: diffInCompletedAt });
    this.isTicketClosedBeforeTwoDays = !!completedAt && diffInCompletedAt > 2;
  }

  async fetchActiveExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
    this.experiments.forEach((exp: any): void => {
      if (exp.key === 'call_back_feature') {
        this.callBackExperimentEnable = true;
      }
      if (exp.key === 'new_help_tree') {
        this.newSupportQuestionId = exp.variant.questionId;
        this.newHelpTreeExperiment = true;
      }
      if (exp.key === 'cross_sell_regimen_v2') {
        this.crossSellRegimenV2Experiment = exp;
      }
      if (exp.key === 'unpaid_user_pending_call_banner') {
        this.unpaidUserDoctorCallExperiment = true;
      }
      if (exp.key === 'regimen_expired_below_3k') {
        this.isRegimenExpiredExperimentEnabled = true;
      }
      if (exp.key === "acq_rebranding_2025") {
        this.acqRebranding2025 = true;
      }
      if (exp.key === 'chat_response_time') {
        this.botReplyTimeSeries = exp.variant?.response_time;
      }
      if (exp.key === 'skin_report_load_fixes') {
        this.isSkinReportLoadFixesEnabled = true;
      }
      if (exp.key === 'chat_skip_last_questions') {
        this.chatSkipLastQuestionsExperiment = exp;
      }
    });
  }

  initialize(): void {
    this.ui = {
      chatInputEnable: false,
      botReplyInProcess: false,
      userResponseSaveInProcess: false,
      enableScrollToBottom: false,
      botReplyTimeSeries: { ...this.botReplyTimeSeries},
    };
    this.responseModes = this.appConfig.Shared.Assistant.ResponseModes;
  }

  addToChats(top: boolean, chats: Array<any>, fromAssistantReply?: boolean): void {
    if (!chats.length) return;
    if (top) {
      chats.reverse();
      this.recordScrollPosition();
    }
    chats.sort((item1: any, item2: any): number => {
      const t1 = item1.get('createdAt') ? item1.get('createdAt')
        .getTime() : new Date().getTime();
      const t2 = item2.get('createdAt') ? item2.get('createdAt')
        .getTime() : new Date().getTime();
      if (t1 === t2) {
        return 0;
      }
      return t1 > t2 ? 1 : -1;
    });
    this.loadChatProgress(chats);
    let prevImage;
    chats.forEach(async (chat_: any, index: number): Promise<void> => {
      const chat = chat_;
      if (chat.get('showAfterTime') && !this.conn.isInternalUser()) { // if message has to be shown after certain time

        this.regimenService.sendRegimenAllocatedEventForIOS(this.user?.get('Age'));

        const timeLeft = (new Date(chat.get('showAfterTime')).getTime() - new Date().getTime());
        this.openActiveSessionRegimen();
        if (timeLeft > 0) setTimeout((): void => this.startRegimenPreparationTimer(timeLeft, chat), 0);
      }
      chat.displayDate = this.getDateFormatted(chat.get('createdAt'));
      if (this.isInternalUser) {
        if (chat.get('MessageInEnglish')) chat.set('Message', chat.get('MessageInEnglish'));
      }

      if (chat?.get('questionId') === 'ProgressCheckFaceFinalOk') {
        this.followUpChatCompleted = true;
        this.localStorageService.delete('sideFaceUploadSkipped');
      }

      /**
       * If any chats are found to be created by (Owner != USER) after the lastVisitedTime of ticket,
       * Then we set 'unreadMessageStart' with length of chats below it (which length of unread messages).
       * Then we delete 'lastVisitedTime'. All needed data is set, so we delete it and let forEach continue.
       *
       */
      if (this.lastVisitedTime && chat.get('createdAt') > this.lastVisitedTime) {
        if (index > 0 && chats[index - 1].get('Owner') !== this.appConfig.Shared.Assistant.Owner.USER) {
          chat.set('unreadMessageStart', chats
            .slice(index, chats.length)
            .filter((each: any): boolean => ![this.appConfig.Shared.Assistant.Mode.SMS_COPY].includes(each.get('Mode')))
            .length);
        }
        delete this.lastVisitedTime;
      }

      if (chat.get('Mode') === this.appConfig.Shared.Assistant.Mode.REQUEST_IMAGE_RESPONSE_BOTH_SIDE_FACE) {
        const imageTypeMapping = chat.get('imageTypeMapping');
        const tempUrlArray = chat.get('tempUrl')?.split(',');
        if (tempUrlArray.length > 1) {
          if (imageTypeMapping?.LEFT_SIDE_FACE && imageTypeMapping?.RIGHT_SIDE_FACE) {
            const leftPath  = this.getGcsPath(imageTypeMapping.LEFT_SIDE_FACE);
            const rightPath = this.getGcsPath(imageTypeMapping.RIGHT_SIDE_FACE);
            const leftSignedUrl  = tempUrlArray.find(signedUrl =>
              this.getGcsPath(signedUrl.split('?')[0]) === leftPath);
            const rightSignedUrl = tempUrlArray.find(signedUrl =>
              this.getGcsPath(signedUrl.split('?')[0]) === rightPath);
            chat.set('bothSideTempUrls', leftSignedUrl && rightSignedUrl ?
              [ leftSignedUrl, rightSignedUrl ] : [tempUrlArray[1], tempUrlArray[0]]);
            if (await this.isImageDeleted(leftSignedUrl)) chat.set('leftSignedUrlDeleted', true)
            if (await this.isImageDeleted(rightSignedUrl)) chat.set('rightSignedUrlDeleted', true)
          } else {
            chat.set('bothSideTempUrls', [ tempUrlArray[1], tempUrlArray[0] ]);
            prevImage = chat;
          }
        } else {
          chat.set('Mode', prevImage ? this.appConfig.Shared.Assistant.Mode.BLOCK : this.appConfig.Shared.Assistant.Mode.IMAGE_CLUBBED);
          prevImage = prevImage ?? chat;
        }
        const imgArr = prevImage.get('imageArray') ?? [];
        imgArr?.push(chat);
        prevImage.set('imageArray', imgArr);
        prevImage.displayDate = chat.displayDate;
      }

      if (chat.get('Mode') === this.appConfig.Shared.Assistant.Mode.REQUEST_IMAGE_RESPONSE) {
        if (prevImage) {
          chat.set('Mode', this.appConfig.Shared.Assistant.Mode.BLOCK);
        } else {
          prevImage = chat;
          prevImage.set('Mode', this.appConfig.Shared.Assistant.Mode.IMAGE_CLUBBED);
        }
        const imgArr = prevImage.get('imageArray') || [];
        imgArr?.push(chat);
        prevImage.set('imageArray', imgArr);
        prevImage.displayDate = chat.displayDate;
      } else if (![this.appConfig.Shared.Assistant.Mode.BLOCK, this.appConfig.Shared.Assistant.Mode.UNBLOCK].includes(chat.get('Mode'))) {
        prevImage = undefined;
      }
    });

    this.consultationSessionService.setChats(chats);

    this.zone.run((): number => this.chats.push(...chats));
  }

  getGcsPath(urlString: string) {
    let url: URL;
    try {
      url = new URL(urlString);
    } catch (e) {
      url = new URL('https://' + urlString);
    }
    return url.pathname;
  }

  expandImages(chat: any): void {
    this.window.replaceHash('./#images', (): void => {
      this.multiImageData = [];
    });
    this.multiImageData = chat.get('imageArray');
  }

  openImageModal(chat:any): void {
    this.imageModal = {
      open: true,
      imageUrl: chat.get('Message'),
    };
  }

  closeImageModal(): void {
    this.imageModal = {
      open: false,
    };
  }

  handelDownload(): void {
    const imgSrc = this.imageModal.imageUrl; // Get the image source
    // Fetch the image as a blob
    fetch(imgSrc)
      .then((response:any): void => response.blob())
      .then((blob:any): void => {
        // Create a temporary anchor element
        const link = this.document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'image.jpeg'; // Set the desired file name
        // Simulate click on the link to trigger the download
        link.click();
        // Clean up - remove the Object URL after the download
        URL.revokeObjectURL(link.href);
      })
      .catch((error:any): void => {
        console.error('Error fetching the image:', error);
      });
  }

  handelShare(): void {
    this.broadcast.broadcast('SHARE', { type: 'IMAGE_ONLY', url: this.imageModal.imageUrl });
  }

  getDateFormatted(chatDate: any): string {
    const today = new Date();
    const date = chatDate ? new Date(chatDate) : new Date();
    let dateString = `${this.months[date.getMonth()]} ${date.getDate()}`;
    if (date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
      dateString = 'Today';
    } else if (this.isInternalUser) dateString = `${dateString} ${date.getFullYear()}`;
    return dateString;
  }

  getLatestMessage(skipSMSCopy: boolean = false): any {
    let index = this.chats.length - 1;
    while (index >= 0) {
      if (skipSMSCopy && this.chats[index].get('Mode') === this.appConfig.Shared.Assistant.Mode.SMS_COPY) {
        index -= 1;
      } else {
        break;
      }
    }
    return this.chats[index];
  }

  async loadNewChat(): Promise<any> {
    if (!this.user || this.chatPayload.loading.new) return;
    this.chatPayload.loading.new = true;
    const where: any = { ...this.where };
    const latestMessage = this.getLatestMessage();
    if (latestMessage) {
      where.createdAt = { $gt: latestMessage.get('createdAt') };
    }
    try {
      const data = await this.conn[this.dataFunctionName]({
        where,
        ascending: 'createdAt',
      });
      if (data?.length && data?.[0].get('questionId') === 'ProgressCheckFaceFinalOk') {
        const notificationExperiment = this.experiments.find((item:any):any => item.key === 'notification_permission_popup');
        if (notificationExperiment) {
          setTimeout((): void => this.openBottomSheet(), 2000);
        }
      }
      if (this.selectedChatType?.className === 'SupportTicket' && data?.length
        && data[0].get('Owner') === this.appConfig.Shared.Assistant.Owner.BOT
        && !data[0].get('Inputs')?.length) {
        this.selectedChatType = await this.conn.findSupportTicketById(this.route.snapshot.params.id, { translate: true });
      }
      if (data.length === 0
        && latestMessage
        && latestMessage.get('messageAttendedByBot')
        && latestMessage.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER) {
        this.startBotReplyTimer(true);
        return;
      }
      if (data.length === 1 && this.isWhatDoCureskinMembersSayMessage(data[0])) {
        this.startBotReplyTimer(true);
        return;
      }
      this.cancelBotReplyWaitingTimer();
      await this.addToChats(false, data, true);
      this.processDataAfterFetch();
      this.chatPayload.loading.new = false;
      this.chatPayload.loading.old = false;
      this.chatLoadingStatus.emit(false);
      this.scrollChatToBottom();
    } catch (err) {
      if (err instanceof Error) {
        this.sentry.handleError(err);
      }
      this.chatPayload.loading.new = false;
      this.cancelBotReplyWaitingTimer();
      this.showError(err.message || err.toString());
    }
  }

  openBottomSheet(): void {
    // call function to check permission status true or false
    // if true then perimssionn already given
    this.appBridge.requestNotificationStatus().then((status:boolean) => {
      this.isNotificationPermissionEnabled = status;
      if (!this.isNotificationPermissionEnabled) {
        this.bottomSheet.open(NotificationPermissionSheetComponent, {
          data: {
            content: 'followup-completed',
            callback: (): void => {
              this.openNativePermissionPopup();
            },
          },
        });
      }
    })
  }

  openNativePermissionPopup(): void {
    this.appBridge.requestNotificationPermission();
  }

  openLanguageChangeDrawer(): void {
    if (this.ui.botReplyInProcess) return;
    const botChats: Array<Table.ConsultationSessionChat> = (this.consultationSessionService.getChats()
      .filter((chat: any): boolean => chat.get('Owner') === 'BOT'));
    const latestBotChat = botChats.length > 0 ? botChats[botChats.length - 1] : undefined;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'chat-language-change-pill' }));
    this.bottomSheet.open(LanguageChangeComponent, {
      data: {
        translateLastBotQuestionData: {
          questionId: latestBotChat?.get('questionId'),
          regimenClass: this.selectedChatType?.get('PrivateMainConcernClass'),
        },
        translateChatMessages: true,
      },
    });
  }

  async loadOldChat(): Promise<any> {
    if (!this.user || this.chatPayload.loading.old) return;
    this.chatPayload.loading.old = true;
    const initialLoad: boolean = (this.chats.length === 0);
    const where: any = { ...this.where };
    if (this.chats.length) {
      where.createdAt = { $lt: this.chats[0].get('createdAt') };
    }
    try {
      const data = await this.conn[this.dataFunctionName]({
        where,
        descending: ['createdAt'],
        limit: this.chatPayload.limit,
        option: { context: { translate: true } },
      });
      if (!data.length) {
        this.chatPayload.loading.old = false;
        if (!this.dataLoadedTwice) {
          setTimeout((): void => { this.loadOldChat(); }, 1000);
          this.dataLoadedTwice = true;
        }
        return;
      }
      await this.addToChats(true, data);
      this.processDataAfterFetch(true, !initialLoad);
      this.chatPayload.loading.old = false;
      this.chatLoadingStatus.emit(false);
    } catch (err) {
      if (err instanceof Error) {
        this.sentry.handleError(err);
      }
      this.chatPayload.loading.old = false;
      this.showError(err.message || err.toString());
    }
  }

  isBotReplyDelayed(message: any): boolean {
    const latestMessage = message;
    if (latestMessage // if bot didn't reply for user message for more than 5 minutes, then open chat input.
      && latestMessage.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER
      && latestMessage.get('messageAttendedByBot')) {
      const lastMessageTime: any = new Date(latestMessage.get('createdAt')).getTime();
      const minutes = (new Date().getTime() - lastMessageTime) / 1000 / 60;
      if (minutes >= 5) {
        this.cancelBotReplyWaitingTimer();
        this.ui.chatInputEnable = true;
        return true;
      }
    }
    return false;
  }

  enableChatInput(): boolean {
    const message = this.latestMessage;
    if (this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.Completed) {
      if (this.callBackExperimentEnable) {
        this.disableChatInput = false;
      }
      return false;
    }
    if (this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.Pending
        && this.selectedChatType?.get('treeCompleted') && this.callBackExperimentEnable
    ) {
      this.disableChatInput = true;
      return false;
    }
    if ((!message || message.get('blockInputAfterMessage') === false) && !this.selectedChatType.get('disableChat')) return true;
    if (this.isBotReplyDelayed(message)) return true;
    if (message?.get('blockInputAfterMessage')) return false;
    if ((message?.get('Inputs') && message.get('Inputs')?.length)) {
      return false;
    }
    return !(this.disableChat || this.selectedChatType.get('disableChat'));
  }

  processDataAfterFetch(scrollToUnreadMessage?: boolean, restoreScroll?: boolean): void {
    this.zone.run((): void => {
      this.latestMessage = this.getLatestMessage(true);
      this.ui.chatInputEnable = this.enableChatInput();
      if (restoreScroll) {
        this.restoreScrollPosition();
      } else {
        this.scrollChatToBottom(scrollToUnreadMessage);
      }
    });
  }

  convertTextToHtml(text: string): string {
    return this.commonUtilityHelper.convertTextToHtml(text);
  }

  scrollChatToBottom(scrollToUnreadMessage?: boolean): void {
    setTimeout((): void => {
      if (scrollToUnreadMessage && this.unreadMessageDivider) {
        this.unreadMessageDivider.nativeElement.parentElement.scrollIntoView();
        return;
      }
      const scrollView = this.messageContainer.nativeElement;
      if (scrollView) {
        this.ui.enableScrollToBottom = false;
        if (scrollView.scroll instanceof Function) {
          scrollView.scroll(0, scrollView.scrollHeight);
        }
        if (scrollView.scrollTop instanceof Function) scrollView.scrollTop = scrollView.scrollHeight;
        const lastVisibleNode = V2ChatComponent.lastVisibleNode(scrollView);
        if ((lastVisibleNode.offsetHeight + lastVisibleNode.offsetTop + this.chatScrollErrorY) > scrollView.offsetHeight) {
          this.chatScrollPossible.emit();
        }
      }
    }, 0);
  }

  onMessageLinkClick(event: MouseEvent): void {
    const target = (event.target as HTMLElement).closest('a');
    if (target) {
      const url = target.getAttribute('href');
      if (url) {
        event.preventDefault();
        this.conn.navigateToURL(url);
      }
    }
  }

  onKeyBoardOpen(): void {
    const scrollView = this.messageContainer.nativeElement;
    setTimeout((): void => {
      if (scrollView.scroll instanceof Function) {
        scrollView.scroll(0, scrollView.scrollHeight);
      }
    }, 0);
  }

  scrollListener(event: any): void {
    if (this.scrollThrottle) return;
    this.updateOnScroll(event);
    this.scrollThrottle = true;
    setTimeout((): boolean => this.scrollThrottle = false, 200);
  }

  updateOnScroll(event: any): void {
    const containerCords = this.messageContainer.nativeElement.getBoundingClientRect();
    if (event.srcElement.scrollTop < (event.srcElement.offsetHeight * 2)
      && !this.chatPayload.noMoreData
      && !this.chatPayload.loading.old) {
      this.loadOldChat();
    }
    if (event.srcElement.scrollHeight - event.srcElement.scrollTop >= (containerCords.height * 2)) {
      this.ui.enableScrollToBottom = true;
    }
    if ((event.srcElement.scrollTop + containerCords.height + 100) >= event.srcElement.scrollHeight) {
      this.ui.enableScrollToBottom = false;
    }
  }

  recordScrollPosition(): void {
    this.ui.chatScrollTop = this.messageContainer.nativeElement.scrollHeight - this.messageContainer.nativeElement.scrollTop;
  }

  restoreScrollPosition(): void {
    if (!this.ui.chatScrollTop) return;
    setTimeout((): void => {
      this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight - this.ui.chatScrollTop;
      delete this.ui.chatScrollTop;
    });
  }

  showError(message: any): void {
    this.broadcast.broadcast('NOTIFY', { message: message.error || message.toString() });
  }

  async onUploadReport(event: any): Promise<void> {
    const file = event.target.files[0];
    if (!file) return;
    if (!this.commonUtilityHelper.isFileSizeLesserThanMaxSize(file.size, 5)) {
      this.broadcast.broadcast('NOTIFY', { message: 'File size should be less than 5MB. Kindly retry.' });
      return;
    }
    const response: { publicURL: string; signedGetURL: string } = await this.conn.uploadFile({
      file,
      bucket: 'OTHER',
      username: this.user.get('username'),
      source: 'app_upload_report',
    });
    const userFile = new Table.UserFiles();
    userFile.set('fileUrl', response.publicURL);
    userFile.set('user', this.user);
    userFile.set('type', this.commonUtilityHelper.findFileType(file.name));
    try {
      await userFile.save({}, { context: { supportTicketId: this.selectedChatType.id } });
      await this.loadNewChat();
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: error.toString() });
    }
  }

  async redirectToNewTicket(): Promise<any> {
    const newQuestionId = (this.newHelpTreeExperiment && this.newSupportQuestionId) ? this.newSupportQuestionId : '8L9eKnSUPe';
    if (this.selectedChatType?.className === 'SupportTicket'
    && this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.Completed) {
      const { supportTicketId }: { supportTicketId: string } = await this.conn.createTicket(newQuestionId);
      await this.router.navigate([`/support/ticket/${supportTicketId}`]);
    }
  }

  async processAssistantAction(assistant: any, index?: any, input?: any): Promise<any> {
    if (!assistant.get('Inputs') || !assistant.get('Inputs').length) return;
    if (this.selectedChatType?.className === 'SupportTicket' && this.route.snapshot.queryParams.reportId) {
      const followUpReport = new Table.FollowUpReport();
      followUpReport.id = this.route.snapshot.queryParams.reportId;
      followUpReport.set('consentFormAsk', false);
      await followUpReport.save();
    }
    if (assistant.get('Inputs')[index].action === 'ACTION') {
      if (assistant?.get('Inputs')[index]?.params?.queryParams) {
        const params = assistant?.get('Inputs')[index]?.params?.queryParams;
        this.photoType = JSON.parse(params).tag;
        if (this.photoType?.includes(this.appConfig.Shared.InstantCheckup.Type.FACE)) {
          this.localStorageService.setValue('expectedFacePhoto', this.photoType);
        }
      }
      await this.singleSelectorService.openInAppAction(
        assistant.get('Inputs')[index],
        assistant.get('context'),
        this.filePickerComponent,
        assistant.id,
        assistant.className,
        async (inputItem: InputType, data: Record<string, unknown>): Promise<void> => {
          switch (inputItem.params.actionName) {
            case 'OrderSelection': {
              if (data.option) {
                const option = data.option as { orderId: string; orderNumber: string; };
                const context = assistant.get('context');
                context.orderId = option.orderId;
                assistant.set('context', context);
                this.saveUserResponse(assistant, option.orderNumber);
              }
              break;
            }
            case 'MultiProductsSelection': {
              if (data.option) {
                const option = data.option;
                const opts = option as Array<{ title: string; objectId: string }>;
                const titles = opts.map(o => o.title).join(', ');
                const productIds = opts.map(o => o.objectId).join(', ');
                const context = assistant.get('context');
                context.productIds = productIds;
                assistant.set('context', context);
                await this.addProductsToCart(opts);
                this.saveUserResponse(assistant, titles);
              }
              break;
            }
            case 'ProductSelection': {
              if (data.option) {
                const option = data.option as { productId: string; title: string; };
                const context = assistant.get('context');
                context.catalogId = option.productId;
                assistant.set('context', context);
                this.saveUserResponse(assistant, option.title);
              }
              break;
            }
            default:
          }
        });
    } else if (assistant.get('Inputs')[index].action === 'TREE') {
      const message = assistant.get('Inputs')[index].type === 'TEXT'
        ? input
        : assistant.get('Inputs')[index].text;
      await this.saveUserResponse(assistant, message);
    }
  }

  prepareReply(lastMessage: any, userReply: string): any {
    const reply = new Table[lastMessage?.className](this.where);
    if (lastMessage
      && lastMessage.get('Owner') === this.appConfig.Shared.Assistant.Owner.BOT
      && lastMessage.get('BotMetadata')) {
      reply.set('BotMetadata', `REPLY_${lastMessage.get('BotMetadata')}`);
    }
    let mode = this.appConfig.Shared.Assistant.Mode.TEXT_NOREPLY;
    if (lastMessage && this.responseModes.includes(lastMessage.get('Mode'))) {
      mode = `${lastMessage.get('Mode')}_RESPONSE`;
    }
    if (this.isInternalUser) {
      reply.set('OperatorName', this.currentUser?.get('username'));
    }
    reply.set('user', this.user);
    reply.set('Owner', this.appConfig.Shared.Assistant.Owner.USER);
    reply.set('context', lastMessage.get('context'));
    reply.set('questionId', lastMessage.get('questionId'));
    reply.set('Message', userReply);
    reply.set('Mode', mode);
    reply.set('messageAttendedByBot', lastMessage.get('messageAttendedByBot'));
    reply.set('Owner', this.appConfig.Shared.Assistant.Owner.USER);
    reply.set('UserId', this.user.get('username'));
    reply.set('uniqueId', lastMessage ? `assistant_3_${lastMessage.id}` : uuid());
    return reply;
  }

  async saveUserResponse(prevMessage: any, userReply: string): Promise<any> {
    if (!userReply || this.ui.botReplyInProcess || this.ui.userResponseSaveInProcess) return;
    this.ui.userResponseSaveInProcess = true;
    if (!this.allowAnimation) this.allowAnimation = true;
    const lastMessage = prevMessage || this.getLatestMessage();
    const messageAttendedByBot = lastMessage?.get('messageAttendedByBot');
    const reply = await this.prepareReply(lastMessage, userReply);
    this.addToChats(false, [reply]);
    this.scrollChatToBottom();
    delete this.chatPayload.input;
    if (messageAttendedByBot) {
      this.updateBotReplyProgress(true);
    }
    try {
      await reply.save();
      if (messageAttendedByBot) {
        this.startBotReplyTimer();
      } else {
        setTimeout((): Promise<void> => this.loadNewChat(), 2000);
      }
      this.ui.userResponseSaveInProcess = false;
    } catch (err) {
      this.updateBotReplyProgress(false);
      this.ui.userResponseSaveInProcess = false;
      this.zone.run((): void => this.chats.pop());
      this.showError(err.message || err.toString());
      if (err.code === 412 && err.message.includes('Please answer pending questions.')) {
        await this.loadNewChat();
      } else if (err instanceof Error) {
        this.sentry.handleError(err);
      }
    }
  }

  cancelBotReplyWaitingTimer(retryFetch?: boolean): void {
    this.zone.run((): void => {
      if (this.startTimeOfBotReply && !retryFetch) {
        const time = new Date().getTime() - this.startTimeOfBotReply;
        const lastResponseId = this.getLatestMessage()?.id;
        const lastQuestionId = this.getLatestMessage()?.get('questionId');
        this.eventLogger.trackEvent('BOT_RESPONSE_TIME', { userId: this.user.id, timeInMillSec: time, lastResponseId, lastQuestionId });
        delete this.startTimeOfBotReply;
      }
      if (!retryFetch) {
        this.ui.botReplyTimeSeries = {...this.botReplyTimeSeries};
      }
      this.updateBotReplyProgress(false);
      if (!this.waitForBotReplyTimer) return;
      clearTimeout(this.waitForBotReplyTimer);
      delete this.waitForBotReplyTimer;
    });
  }

  async startBotReplyTimer(log?: boolean): Promise<boolean> {
    return this.zone.run(async (): Promise<boolean> => {
      if (log) {
        this.eventLogger.trackEvent('BOT_RESPONSE_RETRY', {
          userId: this.user.id,
          assistant: this.getLatestMessage(),
        });
      }
      this.cancelBotReplyWaitingTimer(true);
      if (!this.startTimeOfBotReply) {
        this.startTimeOfBotReply = new Date().getTime();
      }
      this.updateBotReplyProgress(true);
      this.chatPayload.loading.new = false;

      const seconds = this.ui.botReplyTimeSeries.t1 + this.ui.botReplyTimeSeries.t2;
      this.waitForBotReplyTimer = setTimeout((): Promise<void> => this.loadNewChat(), seconds);
      this.ui.botReplyTimeSeries.t1 = this.ui.botReplyTimeSeries.t2;
      this.ui.botReplyTimeSeries.t2 = seconds;

      const latestMessage = this.getLatestMessage();
      if (this.isBotReplyDelayed(latestMessage)) return true;
      return false;
    });
  }

  navigateTo(url: string): void {
    if (!url) return;
    let queryString = '';
    if (String(url)
      .toLowerCase()
      .includes('regimen')) {
      queryString += `?class=${this.user.get('PrivateMainConcernClass')}`;
    }
    this.conn.navigateToURL(`${url}${queryString}`);
  }

  /**
   * Calculates the minutes, seconds left and runs the clock timer for every seconds.
   * Opens the regimen once the timer ends and clears the interval.
   * @param {number} milliseconds - no of milliseconds left
   * @param chat
   */
  startRegimenPreparationTimer(milliseconds: number, chat: any): any {
    this.conn.updateExperimentLogsInUser({ regimen_allocation_timer_started: this.user.get('PrivateMainConcernClass') });
    this.eventLogger.trackEvent('regimen_allocation_timer_started', { class: this.user.get('PrivateMainConcernClass') });
    this.zone.run((): void => {
      if (milliseconds <= 0) return;
      const minutes = Math.floor(milliseconds / 1000 / 60);
      this.timer.minutes = (minutes > 2) ? 1 : minutes;
      this.timer.seconds = Math.floor((milliseconds - (minutes * 1000 * 60)) / 1000);
      this.timer.time = chat.get('createdAt');
      this.interval = setInterval(async (): Promise<void> => {
        if (this.timer.seconds - 1 >= 0) this.timer.seconds -= 1;
        if (this.timer.seconds - 1 < 0) {
          this.timer.minutes -= 1;
          this.timer.seconds = 59;
        }
        if (this.timer.minutes < 0) {
          clearInterval(this.interval);
          this.timer = {};
          if (this.unpaidUserDoctorCallExperiment) {
            this.eventLogger.trackEvent('regimen_allocated_request_doctor_call', {class: this.user.get('PrivateMainConcernClass')});
          }
        }
      }, 1000);
    });
    this.localStorageService.delete('sideFaceUploadSkipped');
  }

  isChatVisible(chat: any): boolean {
    return ![this.appConfig.Shared.Assistant.Mode.BLOCK].includes(chat.get('Mode'));
  }

  async openActiveSessionRegimen(fetchRegimen?: boolean): Promise<any> {
    if (fetchRegimen) await this.conn.fetchRegimens(null, true);
    this.scrollChatToBottom();
    this.router.navigate(['/regimen'], {
      queryParams: {
        tab: 'regimen',
        class: this.selectedChatType.get('PrivateMainConcernClass'),
        showTimer: true,
      }
    })
  }

  trackChatArray(index: number): number {
    return index;
  }

  home(): void {
    const queryParams: any = { tab: 'home' };
    this.router.navigate(['/user'], { queryParams });
  }

  back(): void {
    const moreInsightsBottomsheetShown: boolean = this.localStorageService.getBooleanValue('moreInsightsBottomsheetShown');
    if (this.selectedChatType?.className === 'FollowUp' && !moreInsightsBottomsheetShown && this.fromSelfCheckupBanner) {
      const followupReportGenerating =
        this.followUp?.get('State') === this.appConfig.Shared.Followup.State.PENDING && this.followUp?.get('ready');
      if (followupReportGenerating && !this.followUpChatCompleted) {
        const sheetRef: MatBottomSheetRef<MoreInsightsBottomsheetComponent> = this.bottomSheet.open(MoreInsightsBottomsheetComponent);
        sheetRef.backdropClick().pipe(take(1)).subscribe(() => sheetRef.dismiss());
        return;
      }
    }
    this.localStorageService.delete('moreInsightsBottomsheetShown');
    if (this.selectedChatType.className === 'SupportTicket') {
      const queryParams: any = { returnedFrom: 'supportTicket' };
      this.router.navigate(['/support'], { queryParams });
      return;
    }
    if (this.selectedChatType.className === 'ConsultationSession') {
      const sheetRef = this.consultationSessionService.validateAndOpenSkipConsultationQuestionsSheet(
        this.userFirstName,
        this.chatSkipLastQuestionsExperiment,
        this.selectedChatType,
      );
      if (sheetRef) {
        return;
      }
    }
    const queryParams: any = { tab: 'home' };
    this.router.navigate(['/user'], { queryParams });
    this.eventLogger.cleverTapEvent('clickedBack', JSON.stringify({ name: 'home' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  handleBackPress(): boolean {
    this.back();
    return true;
  }

  /**
   * Logs the last visited time of that ticket in the ticket table.
   */
  ngOnDestroy(): void {
    if (!this.isInternalUser && this.selectedChatType?.className === 'SupportTicket') {
      this.conn.updateUserLastActiveDetails(this.selectedChatType.id);
    }
    this.currentComponent.remove(this);
    this.cancelBotReplyWaitingTimer();
    clearInterval(this.interval);
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.subscriptions = [];
  }

  private static lastVisibleNode(nativeElement: any): any {
    let index = nativeElement.childNodes.length - 1;
    while (index > 0) {
      if (nativeElement.childNodes[index].nodeName === 'DIV') {
        break;
      }
      index -= 1;
    }
    return nativeElement.childNodes[index];
  }

  async updateFeedback(isHelpfull: boolean, userQuery?: string): Promise<void> {
    if (!isHelpfull) {
      this.eventLogger.trackEvent('support-ticket-dislike');
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'support-ticket-dislike' }));
    } else {
      this.eventLogger.trackEvent('support-ticket-like');
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'support-ticket-like' }));
    }
    if (this.isInternalUser) return;
    this.isHelpfull = isHelpfull;
    const result = await this.conn.updateSupportTicketFeedback(this.selectedChatType.id, isHelpfull, userQuery, true);
    if (result.ticketStatusChanged) {
      this.selectedChatType = await this.conn.findSupportTicketById(this.route.snapshot.params.id, { translate: true });
      if (this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.Pending) await this.loadNewChat();
      return;
    }
    this.afterSaveFeedback = true;
  }

  updateFeedBackIfFalse(): any {
    this.eventLogger.trackEvent('support-ticket-dislike');
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'support-ticket-dislike' }));
    this.bottomSheet.open(GetFeedbackSheetComponent, {
      data: {
        updateFeedbackAndCreatePendingCall: async (createCall: boolean, userQuery?: string): Promise<void> => {
          if (createCall) {
            await this.updateFeedbackAndCreatePendingCall(this.isHelpfull, userQuery);
            this.bottomSheet.dismiss();
          } else {
            await this.updateFeedback(this.isHelpfull, userQuery);
            this.bottomSheet.dismiss();
          }
        },
      },
    });
  }

  async updateFeedbackAndCreatePendingCall(isHelpfull: boolean, userQuery?: string): Promise<void> {
    if (this.isInternalUser) return;
    this.isHelpfull = isHelpfull;
    const result = await this.conn.createPendingCallAndSaveNote({ message: userQuery,
      type: ApiClientConstant.PendingCall.Type.UserRequestedCall,
      ticketId: this.selectedChatType?.id,
      user: this.user });
    await this.conn.updateSupportTicketFeedback(this.selectedChatType.id, false, userQuery, false);
    if (result.message === 'success') {
      this.selectedChatType = await this.conn.findSupportTicketById(this.route.snapshot.params.id, { translate: true });
      if (this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.Pending) await this.loadNewChat();
      return;
    }
    this.bottomSheet.dismiss();
    this.afterSaveFeedback = true;
  }

  async openImageFromBothSidePhotos(imgUrl: string): Promise<boolean | void> {
    const instantCheckup = await this.conn.fetchInstantCheckupByImageUrl(this.conn.getActingUser(), imgUrl);
    const checkupId = instantCheckup?.id;
    if (!instantCheckup || !checkupId) return
    return this.router.navigate([`/user/instantCheckup/${checkupId}`]);
  }

  getExpectedPhotoType(chat: any): string {
    if (chat?.get('questionId') === 'TakeInstantCheckup') {
      return this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;
    }
    if (chat?.get('questionId') === 'TakeInstantCheckSideFace') {
      return this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE;
    }
    if (chat?.get('questionId') === 'HairfallPhoto') {
      return this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT;
    }
    if (chat?.get('questionId') === 'HairfallPhotoTop') {
      return this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP;
    }
    return this.appConfig.Shared.InstantCheckup.Type.FACE;
  }

  handleImageCardClick(chat: any):void {
    const expectedPhotoType = this.getExpectedPhotoType(chat);
    if (expectedPhotoType.includes(this.appConfig.Shared.InstantCheckup.Type.FACE) || expectedPhotoType.includes(this.appConfig.Shared.InstantCheckup.Type.HAIR)) {
      this.localStorageService.setValue('expectedFacePhoto', expectedPhotoType);
    }
    this.setRedirectUrl();
  }

  setRedirectUrl(): void {
    let redirectUrl = this.router.url;
    redirectUrl += `${redirectUrl.includes('?') ? '&' : '?'}back=home`;
    this.localStorageService.setValue('CureSkin/redirectUrl', redirectUrl);
  }

  loadChatProgress(chats: any[]): void {
    this.chatProgressService.setSelectedChatType(this.selectedChatType.className);
    chats
      .filter((chat: any): boolean => chat.attributes.Owner === this.appConfig.Shared.Assistant.Owner.USER)
      .forEach((chat: any): void => {
        const { questionId }: { questionId: string } = chat.attributes;
        const chatTypeKey: string = `${this.selectedChatType.get('PrivateMainConcernClass')}-${questionId}`;

        // Before 90% if questionId is null, undefined, or already processed, skip processing
        if (this.progress < 90) {
          if (questionId === null || questionId === undefined || this.processedQuestions.has(chatTypeKey)) {
            return;
          }
        }

        this.processedQuestions.add(chatTypeKey); // Mark this questionId as processed for this chat type

        const configItem = Object.values(this.progressConfig).find(
          (item: any): boolean => item.questionId === questionId,
        );

        if (configItem) {
          if (configItem.questionType === this.appConfig.Shared.Assistant.QuestionType.ANCHOR) {
            if (configItem.progressValue !== null) {
              if (configItem.progressValue < 98 && configItem.progressValue > this.progress) {
                // Case 1: If progressValue is less than 98 and greater than current progress
                this.progress = configItem.progressValue;
                this.chatProgressService.setChatProgressState(this.progress);
              } else if (configItem.progressValue <= this.progress) {
                // Case 2: If progressValue is less than or equal to current progress, increment by 5%
                this.progress = Math.min(this.progress + 5, 98);
                this.chatProgressService.setChatProgressState(this.progress);
              } else if (configItem.progressValue > 98) {
                // Case 3: If progressValue is greater than 98, do not increment progress
                this.progress = Math.min(this.progress, 98);
              } else if (this.progress >= 90 && this.progress <= 98) {
                // Case 4: Handle progress when between 90% and 98%
                this.progress = Math.min(this.progress + 2, 98);
                this.chatProgressService.setChatProgressState(this.progress);
              }
            } else {
              // Case 5: Handle null progressValue for Anchor type
              this.progress = Math.min(this.progress + 5, 98);
              this.chatProgressService.setChatProgressState(this.progress);
            }
          } else if (configItem.questionType === this.appConfig.Shared.Assistant.QuestionType.INFO) {
            this.progress = Math.min(this.progress, 98);
            this.chatProgressService.setChatProgressState(this.progress);
          }
        } else {
          // Handle cases where questionId is not in the ProgressConfig
          // eslint-disable-next-line no-lonely-if
          if (this.progress < 90) {
            this.progress = Math.min(this.progress + 5, 98);
            this.chatProgressService.setChatProgressState(this.progress);
          } else if (this.progress >= 90 && this.progress < 98) {
            this.progress = Math.min(this.progress + 2, 98);
            this.chatProgressService.setChatProgressState(this.progress);
          }
        }
      });

    chats
      .filter((chat: any): boolean => chat.attributes.Owner === this.appConfig.Shared.Assistant.Owner.BOT)
      .forEach((chat: any): void => {
        if (chat.attributes.uniqueId?.includes('regimen_delay')) {
          this.progress = Math.min(95, 98);
          this.chatProgressService.setChatProgressState(this.progress);
        }
      });

    this.progress = Math.max(5, Math.min(this.progress, 98));
  }
  updateBotReplyProgress(isBotReplyInPorcess: boolean): void {
    this.ui.botReplyInProcess = isBotReplyInPorcess;
    this.botReplyInProcessChange.emit(isBotReplyInPorcess);
  }

  formatTime(dateString: string): string {
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    hours = hours || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr}${ampm}`;
  }

  async isImageDeleted(src: any): Promise<any> {
    const result: any = await fetch(src).catch((): Object => ({ status: 500 }));
    return result.status === 404;
  }

  get isRegimenExpired(): boolean {
    return this.regimenExpiryService.shouldShowRegimenExpiredBanner({
      isExperimentEnabled: this.isRegimenExpiredExperimentEnabled,
      regimens: [],
      totalAmountSpent: this.user.get('totalAmountSpent'),
      isPaidUser: this.isPaidUser,
      consultationSession: undefined,
      skipHide: true
    });
  }

  private isWhatDoCureskinMembersSayMessage(message: any): boolean {
    if (this.isSkinReportLoadFixesEnabled) {
      return message?.get('questionId') === this.whatDoCureskinMembersSayQuestionId;
    }
    return message?.get('MessageInEnglish') === this.whatDoCureskinMembersSayMessageInEnglish;
  }

  private async addProductsToCart(
    products: Array<{ title: string; objectId: string }>
  ): Promise<void> {
    for (const product of products) {
      const params = {
        productId: product.objectId,
        quantity: 1,
        section: 'reOrderTree',
      };

      await this.conn.addProductToNewCart(params);
    }
  }
}
